import type { components } from '../api/schema';

export type Session = components['schemas']['SessionDto'];

/** Versiones anteriores guardaban aquí el refresh token: se canjea una vez y se borra. */
const LEGACY_REFRESH_KEY = 'eatherix.refreshToken';
/** Solo una marca de "hay sesión" para decidir si vale la pena renovar al cargar; no es secreta. */
const SESSION_HINT_KEY = 'eatherix.session';
const API_BASE = import.meta.env.VITE_API_URL ?? '';

/** Cabecera con la que el panel pide la sesión en cookie HttpOnly (ver session-cookie.ts del API). */
export const SESSION_COOKIE_HEADER = 'X-Session-Cookie';

type Listener = (session: Session | null) => void;

/**
 * Custodia de credenciales:
 *  - access token SOLO en memoria (no expuesto a XSS persistente),
 *  - refresh token en una cookie HttpOnly que pone el API: ningún script del panel la lee,
 *  - una única renovación en vuelo: el backend rota el refresh token y reutilizar uno viejo
 *    revoca la sesión, así que N peticiones que reciben 401 a la vez deben esperar la misma.
 */
class TokenStore {
  private accessToken: string | null = null;
  private refreshing: Promise<Session | null> | null = null;
  private readonly listeners = new Set<Listener>();

  get access(): string | null {
    return this.accessToken;
  }

  /** Puede haber una sesión que restaurar (cookie emitida antes o token de una versión vieja). */
  get mayHaveSession(): boolean {
    return read(SESSION_HINT_KEY) !== null || read(LEGACY_REFRESH_KEY) !== null;
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  setSession(session: Session): void {
    this.accessToken = session.accessToken;
    write(SESSION_HINT_KEY, '1');
    write(LEGACY_REFRESH_KEY, null);
    this.listeners.forEach((l) => l(session));
  }

  clear(): void {
    this.accessToken = null;
    write(SESSION_HINT_KEY, null);
    write(LEGACY_REFRESH_KEY, null);
    this.listeners.forEach((l) => l(null));
  }

  /** Renueva la sesión con la cookie (single-flight). Devuelve null si ya no es válida. */
  refresh(): Promise<Session | null> {
    if (this.refreshing) return this.refreshing;
    if (!this.mayHaveSession) return Promise.resolve(null);
    // Migración: el token que guardaba la versión anterior se canjea una sola vez por la cookie.
    const legacy = read(LEGACY_REFRESH_KEY);
    this.refreshing = fetch(`${API_BASE}/v1/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', [SESSION_COOKIE_HEADER]: '1' },
      body: JSON.stringify(legacy ? { refreshToken: legacy } : {}),
    })
      .then(async (response) => {
        if (legacy) write(LEGACY_REFRESH_KEY, null);
        if (!response.ok) {
          this.clear();
          return null;
        }
        const session = (await response.json()) as Session;
        this.setSession(session);
        return session;
      })
      .catch(() => null)
      .finally(() => {
        this.refreshing = null;
      });
    return this.refreshing;
  }
}

function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string | null): void {
  try {
    if (value) localStorage.setItem(key, value);
    else localStorage.removeItem(key);
  } catch {
    // almacenamiento no disponible (modo privado): la sesión se renueva igual con la cookie
  }
}

export const tokens = new TokenStore();
